/**
 * Map Editor Toolbar — mode switcher, undo/redo, save indicator
 */

import { useNavigate } from 'react-router-dom';
import { useMapEditor, type EditorMode } from '../MapEditorProvider';

const MODES: { key: EditorMode; label: string; shortcut: string; icon: JSX.Element }[] = [
  {
    key: 'select', label: 'Select', shortcut: 'V',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" /><path d="M13 13l6 6" /></svg>,
  },
  {
    key: 'move', label: 'Move', shortcut: 'M',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 9 2 12 5 15" /><polyline points="9 5 12 2 15 5" /><polyline points="15 19 12 22 9 19" /><polyline points="19 9 22 12 19 15" /><line x1="2" y1="12" x2="22" y2="12" /><line x1="12" y1="2" x2="12" y2="22" /></svg>,
  },
  {
    key: 'place-photo', label: 'Photo', shortcut: 'P',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>,
  },
  {
    key: 'place-overlay', label: 'Overlay', shortcut: 'O',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>,
  },
];

const SAVE_COLORS = {
  saved: '#22c55e',
  saving: '#94a3b8',
  unsaved: '#f59e0b',
  error: '#ef4444',
};

const SAVE_LABELS = {
  saved: 'Saved',
  saving: 'Saving...',
  unsaved: 'Unsaved',
  error: 'Save failed',
};

export function EditorToolbar() {
  const { state, dispatch, undo, redo, canUndo, canRedo } = useMapEditor();
  const navigate = useNavigate();

  return (
    <div style={styles.toolbar}>
      {/* Back button */}
      <button onClick={() => navigate(-1)} style={styles.backBtn} title="Back to project">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" /><polyline points="12 19 5 12 12 5" />
        </svg>
      </button>

      <div style={styles.divider} />

      {/* Mode switcher */}
      <div style={styles.modeGroup}>
        {MODES.map((m) => (
          <button
            key={m.key}
            onClick={() => dispatch({ type: 'SET_MODE', mode: m.key })}
            style={{
              ...styles.modeBtn,
              ...(state.mode === m.key ? styles.modeBtnActive : {}),
            }}
            title={`${m.label} (${m.shortcut})`}
          >
            {m.icon}
            <span style={styles.modeBtnLabel}>{m.label}</span>
          </button>
        ))}
      </div>

      <div style={styles.divider} />

      {/* Undo / Redo */}
      <button onClick={undo} disabled={!canUndo} style={{ ...styles.iconBtn, opacity: canUndo ? 1 : 0.3 }} title="Undo (Ctrl+Z)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
        </svg>
      </button>
      <button onClick={redo} disabled={!canRedo} style={{ ...styles.iconBtn, opacity: canRedo ? 1 : 0.3 }} title="Redo (Ctrl+Shift+Z)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
        </svg>
      </button>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Save indicator */}
      <div style={styles.saveIndicator}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: SAVE_COLORS[state.saveStatus] }} />
        <span style={{ color: SAVE_COLORS[state.saveStatus], fontSize: 12, fontWeight: 500 }}>
          {SAVE_LABELS[state.saveStatus]}
        </span>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  toolbar: {
    height: 48,
    background: 'rgba(13,13,13,0.95)',
    borderBottom: '1px solid #333',
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    gap: 4,
    flexShrink: 0,
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
  },
  divider: {
    width: 1,
    height: 24,
    background: '#333',
    margin: '0 8px',
  },
  modeGroup: {
    display: 'flex',
    background: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: 8,
    overflow: 'hidden',
  },
  modeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    border: 'none',
    background: 'transparent',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    transition: 'background 0.1s, color 0.1s',
  },
  modeBtnActive: {
    background: '#334155',
    color: '#f1f5f9',
  },
  modeBtnLabel: {
    // Hidden on narrow screens if needed
  },
  iconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
  },
  saveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
  },
};
